import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PayrollApproval = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetails, setRunDetails] = useState([]);
  const [variance, setVariance] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/runs?company_id=${selectedCompany}`, { withCredentials: true });
      setRuns(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchRuns(); }, [selectedCompany, fetchRuns]);



  const fetchDetails = async (runId) => {
    try {
      const [detailsRes, varianceRes] = await Promise.all([
        axios.get(`${API_URL}/payroll/runs/${runId}/details`, { withCredentials: true }),
        axios.get(`${API_URL}/payroll/runs/${runId}/variance`, { withCredentials: true }),
      ]);
      setRunDetails(detailsRes.data);
      setVariance(varianceRes.data);
    } catch (e) { console.error(e); }
  };

  const handleAction = async (runId, action) => {
    const remarks = action === 'reject' ? prompt('Rejection reason:') : '';
    if (action === 'reject' && !remarks) return;
    try {
      await axios.post(`${API_URL}/payroll/runs/${runId}/action`, { action, remarks }, { withCredentials: true });
      fetchRuns();
      setSelectedRun(null);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const pendingRuns = runs.filter(r => r.status === 'pending_approval');
  const approvedRuns = runs.filter(r => ['approved', 'released', 'locked'].includes(r.status));

  return (
    <MainLayout>
      <div data-testid="payroll-approval-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Payroll Approval</h1>
            <p className="text-sm text-slate-500 mt-1">Review and approve payroll runs before release</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="approval-export-btn" onClick={() => {
              if (runs.length === 0) { toast.error('No runs to export'); return; }
              downloadCSV(runs.map(r => ({
                Run: r.run_number, Status: r.status, Employees: r.total_employees, Gross: r.total_gross, Net: r.total_net, Period: r.period_name
              })), 'payroll_approval_export.csv');
              toast.success('Payroll approval data exported');
            }} className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        </div>

        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
          {[['pending', `Pending (${pendingRuns.length})`], ['approved', 'Approved'], ['variance', 'Variance Report']].map(([tab, label]) => (
            <button key={tab} data-testid={`tab-${tab}`} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{label}</button>
          ))}
        </div>

        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingRuns.map((r) => (
              <div key={r.id} data-testid={`approval-card-${r.id}`} className="bg-white rounded-xl border border-orange-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-slate-800">Run #{r.run_number} - {r.run_type}</h3>
                    <p className="text-xs text-slate-500">{r.total_employees} employees</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button data-testid={`approve-run-${r.id}`} onClick={() => handleAction(r.id, 'approve')} className="flex items-center space-x-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700">
                      <CheckCircle className="w-4 h-4" /><span>Approve</span>
                    </button>
                    <button data-testid={`reject-run-${r.id}`} onClick={() => handleAction(r.id, 'reject')} className="flex items-center space-x-1 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700">
                      <XCircle className="w-4 h-4" /><span>Reject</span>
                    </button>
                    <button onClick={() => { setSelectedRun(r); fetchDetails(r.id); }} className="text-sm text-[#0052FF] hover:underline">View Details</button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-slate-50 p-3 rounded-lg text-center"><p className="text-xs text-slate-500">Total Gross</p><p className="text-lg font-bold text-emerald-600">{(r.total_gross || 0).toLocaleString()}</p></div>
                  <div className="bg-slate-50 p-3 rounded-lg text-center"><p className="text-xs text-slate-500">Total Deductions</p><p className="text-lg font-bold text-red-500">{(r.total_deductions || 0).toLocaleString()}</p></div>
                  <div className="bg-slate-50 p-3 rounded-lg text-center"><p className="text-xs text-slate-500">Total Net</p><p className="text-lg font-bold text-[#0052FF]">{(r.total_net || 0).toLocaleString()}</p></div>
                  <div className="bg-slate-50 p-3 rounded-lg text-center"><p className="text-xs text-slate-500">Exceptions</p><p className="text-lg font-bold text-amber-600">{r.exception_count || 0}</p></div>
                </div>
              </div>
            ))}
            {pendingRuns.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400"><Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>No payroll runs pending approval</p></div>}
          </div>
        )}

        {activeTab === 'approved' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Run</th>
                <th className="px-4 py-3 text-left font-medium">Employees</th>
                <th className="px-4 py-3 text-right font-medium">Net Total</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {approvedRuns.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Run #{r.run_number}</td>
                    <td className="px-4 py-3">{r.total_employees}</td>
                    <td className="px-4 py-3 text-right font-mono">{(r.total_net || 0).toLocaleString()}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'variance' && selectedRun && variance.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h2 className="text-base font-semibold text-slate-800">Variance Report (vs Previous Period)</h2></div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-2 text-left font-medium">Employee</th>
                <th className="px-4 py-2 text-right font-medium">Current Gross</th>
                <th className="px-4 py-2 text-right font-medium">Previous Gross</th>
                <th className="px-4 py-2 text-right font-medium">Variance</th>
                <th className="px-4 py-2 text-right font-medium">Current Net</th>
                <th className="px-4 py-2 text-right font-medium">Net Variance</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {variance.map((v, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-800">{v.employee_name}</td>
                    <td className="px-4 py-2 text-right font-mono">{v.current_gross?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-500">{v.previous_gross?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono"><span className={v.gross_variance > 0 ? 'text-green-600' : v.gross_variance < 0 ? 'text-red-600' : ''}>{v.gross_variance > 0 ? '+' : ''}{v.gross_variance?.toLocaleString()}</span></td>
                    <td className="px-4 py-2 text-right font-mono">{v.current_net?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono"><span className={v.net_variance > 0 ? 'text-green-600' : v.net_variance < 0 ? 'text-red-600' : ''}>{v.net_variance > 0 ? '+' : ''}{v.net_variance?.toLocaleString()}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PayrollApproval;
